import React, { useCallback, useMemo, ReactElement } from 'react';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import Widget from '../../../components/widget/widget.component';
import ImageLarge from '../../../components/image-large/image-large.component';
import ImageLargeFormFieldUpload
  from '../../../components/image-large-form-field-upload/image-large-form-field-upload.component';
import ImageControlsBlock from '../../../components/image-controls-block/image-controls-block.component';
import {
  DEAL_EQUITY_ORGANIZATION_CHART_DELETE, DEAL_EQUITY_ORGANIZATION_CHART_EDIT,
} from '../../../constants/entity/permissions';
import NoImage from '../../../static/images/no-image.png';

const DealEquityOrganizationChart: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Equity Organization Chart';
  const WIDGET_NAME: string = 'widget-deal-equity-organization-chart';

  const {
    isEditMode,
    isNewDeal,
    isDealEquityOrganizationChartDeleted,
    dealEquityOrganizationChartData,
    dealEquityOrganizationChartFile,
    onFileChange,
    onFileDelete,
    onEditModeButtonClick,
  } = props;

  const dealEquityOrganizationChartEditPermissions: string[] = useMemo((): string[] => [
    DEAL_EQUITY_ORGANIZATION_CHART_EDIT,
  ], []);
  const dealEquityOrganizationChartDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_EQUITY_ORGANIZATION_CHART_DELETE,
  ], []);

  const formPrefix: string = 'dealData.dealEquityOrganizationChartData';
  const isEmpty: boolean = !isEditMode && !isNewDeal
    && (!dealEquityOrganizationChartData || !dealEquityOrganizationChartData.link);

  const onDealEquityOrganizationChartFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file);
    }
  }, []);

  const onDealEquityOrganizationChartFileDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onFileDelete) {
      onFileDelete();
    }

    event.stopPropagation();
  }, []);

  return (
    <CheckAccess permissions={dealEquityOrganizationChartEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isStaticHeight
        isEmpty={isEmpty}
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealEquityOrganizationChartEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        <FlexContainer isCentered isFullHeight>
          {
            (isEditMode || isNewDeal) ? (
              <CheckAccess permissions={dealEquityOrganizationChartEditPermissions}>
                <div>
                  <CheckAccess permissions={dealEquityOrganizationChartDeletePermissions}>
                    <ImageControlsBlock
                      withDelete={!isDealEquityOrganizationChartDeleted && !dealEquityOrganizationChartFile
                        && !!dealEquityOrganizationChartData && !!dealEquityOrganizationChartData.link}
                      onDeleteClick={onDealEquityOrganizationChartFileDelete}
                    />
                  </CheckAccess>
                  <div>
                    <ImageLargeFormFieldUpload
                      name={`${formPrefix}.file`}
                      uploadFile={dealEquityOrganizationChartFile}
                      buttonLabel="Equity Organization Chart"
                      defaultPreviewUrl={(!isDealEquityOrganizationChartDeleted
                        && dealEquityOrganizationChartData && dealEquityOrganizationChartData.link) || NoImage}
                      onFileChange={onDealEquityOrganizationChartFileChange}
                      onFileDelete={onDealEquityOrganizationChartFileDelete}
                    />
                  </div>
                </div>
              </CheckAccess>
            ) : (
              dealEquityOrganizationChartData && dealEquityOrganizationChartData.link && (
                <FilteredLink isExternal link={dealEquityOrganizationChartData.link}>
                  <ImageLarge
                    text="Equity Organization Chart"
                    link={dealEquityOrganizationChartData.link}
                  />
                </FilteredLink>
              )
            )
          }
        </FlexContainer>
      </Widget>
    </CheckAccess>
  );
});

export default DealEquityOrganizationChart;
